import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Phone, MapPin, Send, Check, X, Loader2, ArrowLeft, Instagram } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import GrainTexture from '@/components/GrainTexture';
import GeoPattern from '@/components/GeoPattern';
import OptimizedImage from '@/components/OptimizedImage';
import contactVisual from '@/assets/systemique/contact-visual.webp';
import { sendSlackNotification } from '@/lib/webhook';
import { SEO } from '@/components/SEO';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ADDRESS, CONTACT, SITE_NAME, SITE_ORIGIN } from '@/seo/siteConfig';

type ButtonState = 'idle' | 'loading' | 'success' | 'error';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonState>('idle');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('🔥 FORM SUBMITTED - Start processing');
    e.preventDefault();
    setIsSubmitting(true);
    setButtonState('loading');
    
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const data = {
      voornaam: formData.get('voornaam') as string,
      achternaam: '',
      email: formData.get('email') as string,
      telefoon: '',
      organisatie: '',
      onderwerp: formData.get('onderwerp') as string || 'algemene-vraag',
      bericht: formData.get('bericht') as string,
      formType: 'Contact'
    };

    try {
      console.log('📤 About to call sendSlackNotification with:', data);
      // Send Slack notification with error handling
      const result = await sendSlackNotification(data);
      
      if (result.success) {
        setButtonState('success');
        toast({
          title: "Bericht verzonden!",
          description: "Bedankt voor je bericht. Ik neem binnen 1 werkdag contact met je op.",
        });
        
        // Reset button after 3 seconds
        setTimeout(() => {
          setButtonState('idle');
        }, 3000);
      } else {
        // Show error message to user
        console.error('Slack notification failed:', result.error);
        setButtonState('error');
        toast({
          title: "Er ging iets mis",
          description: result.error || "Probeer het opnieuw of neem direct contact op via telefoon.",
          variant: "destructive",
        });
        
        // Reset button after 3 seconds
        setTimeout(() => {
          setButtonState('idle');
        }, 3000);
      }
    } catch (error) {
      // Fallback error handling
      console.error('Unexpected error:', error);
      setButtonState('error');
      toast({
        title: "Er ging iets mis",
        description: "Probeer het opnieuw of neem direct contact op via telefoon.",
        variant: "destructive",
      });
      
      // Reset button after 3 seconds
      setTimeout(() => {
        setButtonState('idle');
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Contact | organisatieopstellingen & teamcoaching"
        description="Vraag een offerte of kennismakingsgesprek aan voor organisatieopstellingen, systemische teamcoaching of een leergang. Marthe van der Molen werkt vanuit Castricum, in-house of op locatie in heel Nederland."
        keywords="contact organisatieopstellingen, offerte organisatieopstelling, systemische teamcoaching contact, leergang onderstroom aanmelden, Marthe van der Molen zakelijk contact"
        canonical="https://organisatieopstellingen.com/contact"
      />
      
      {/* Structured Data - Professional Service */}
      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "name": SITE_NAME,
        "description": "Contact voor organisatieopstellingen, systemische teamcoaching en opleidingen voor leiders en teams",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": ADDRESS.locality,
          "addressRegion": ADDRESS.region,
          "addressCountry": ADDRESS.country
        },
        "areaServed": [
          {"@type": "AdministrativeArea", "name": "Noord-Holland"},
          {"@type": "Country", "name": "Nederland"}
        ],
        "serviceType": [
          "Organisatieopstellingen",
          "Systemische teamcoaching",
          "Opleidingen systemisch werk"
        ],
        "priceRange": "$$",
        "telephone": CONTACT.phone,
        "email": CONTACT.email,
        "url": `${SITE_ORIGIN}/contact`
      })}
      </script>
      
      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            Contact
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Vraagstuk in je team of organisatie? Plan een kennismaking of vraag een offerte aan.
          </p>
        </div>
      </section>

      {/* Breadcrumbs */}
      <section className="bg-white py-4 px-6">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs className="text-[#854C73]" />
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-24 px-6 relative bg-white min-h-[60vh] overflow-hidden">
        <GrainTexture opacity={0.15} />
        <GeoPattern />
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-20 items-start">
            
            {/* Linker Kolom - Contact Info (5 kolommen) */}
            <div className="md:col-span-5 space-y-8">
              <div>
                <span className="text-[#854C73] font-bold tracking-widest text-xs mb-4 block uppercase">
                  VERBINDING
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-[#854C73] mb-6">
                  Laten we<br />
                  <span className="italic font-light font-serif text-[#854C73]">kennismaken</span>
                </h2>
                <p className="text-[#6b4912] text-lg font-light leading-relaxed">
                  Op deze site gaat het om zakelijke trajecten: organisatieopstellingen, systemische teamcoaching en opleidingen. Beschrijf kort je vraag. Ik reageer binnen één werkdag met een voorstel of vervolgstap.
                </p>
              </div>

              <div className="group rounded-3xl overflow-hidden shadow-xl">
                <OptimizedImage
                  src={contactVisual}
                  webpSrc={contactVisual}
                  alt="Marthe van der Molen, contact voor organisatieopstellingen en teamcoaching"
                  className="w-full h-auto rounded-3xl transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  width={1600}
                  height={1067}
                />
              </div>

              {/* Contact Items */}
              <div className="space-y-6 mt-12">
                {/* E-mail */}
                <a 
                  href="mailto:marthe@systemique.nl" 
                  className="flex items-start gap-6 group p-4 rounded-lg hover:bg-[#FAEBEE]/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] group-hover:bg-[#854C73] group-hover:text-white transition-all duration-300 flex-shrink-0">
                    <Mail size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[#854C73] text-xs font-bold uppercase tracking-wider mb-1">
                      E-MAIL
                    </p>
                    <p className="font-serif text-xl text-[#854C73]">
                      marthe@systemique.nl
                    </p>
                  </div>
                </a>

                {/* Telefoon */}
                <a 
                  href="tel:+31612991453" 
                  className="flex items-start gap-6 group p-4 rounded-lg hover:bg-[#FAEBEE]/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] group-hover:bg-[#854C73] group-hover:text-white transition-all duration-300 flex-shrink-0">
                    <Phone size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[#854C73] text-xs font-bold uppercase tracking-wider mb-1">
                      TELEFOON
                    </p>
                    <p className="font-serif text-xl text-[#854C73]">
                      +31 6 1299 1453
                    </p>
                  </div>
                </a>

                {/* Locatie */}
                <div className="flex items-start gap-6 p-4 rounded-lg">
                  <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] flex-shrink-0">
                    <MapPin size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[#854C73] text-xs font-bold uppercase tracking-wider mb-1">
                      LOCATIE
                    </p>
                    <p className="font-serif text-xl text-[#854C73]">
                      Eigen praktijk in Castricum<br />
                      <span className="text-base">Noord Holland, 20 min van Amsterdam. Werkgebied heel Nederland</span>
                    </p>
                  </div>
                </div>

                {/* Instagram */}
                <a 
                  href="https://www.instagram.com/systemique.nl/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-6 group p-4 rounded-lg hover:bg-[#FAEBEE]/50 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-[#FAEBEE] rounded-full flex items-center justify-center text-[#854C73] group-hover:bg-[#854C73] group-hover:text-white transition-all duration-300 flex-shrink-0">
                    <Instagram size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[#854C73] text-xs font-bold uppercase tracking-wider mb-1">
                      INSTAGRAM
                    </p>
                    <p className="font-serif text-xl text-[#854C73]">
                      @systemique.nl
                    </p>
                  </div>
                </a>
              </div>
            </div>

            {/* Rechter Kolom - Formulier "Levitation" Stijl (7 kolommen) */}
            <div className="md:col-span-7">
              <div 
                className="relative bg-white rounded-3xl shadow-2xl border border-[#854C73]/5 p-10 md:p-12 transform transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
              >
                {/* Accent Rand - Decoratieve lijn bovenaan */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#854C73] via-[#854C73] to-[#854C73] rounded-t-3xl"></div>

                <h2 className="font-serif text-3xl text-[#854C73] mb-8">
                  Stuur een bericht
                </h2>

                <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                  {/* Naam & Email naast elkaar */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label 
                        htmlFor="voornaam" 
                        className="text-xs font-bold tracking-wider uppercase text-[#6b4912]"
                      >
                        NAAM
                      </Label>
                      <Input
                        id="voornaam"
                        name="voornaam"
                        placeholder="Je naam"
                        required
                        className="mt-1 text-base bg-[#FAEBEE]/30 border-[#854C73]/10 focus:bg-white focus:border-[#854C73] transition-colors"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label 
                        htmlFor="email" 
                        className="text-xs font-bold tracking-wider uppercase text-[#6b4912]"
                      >
                        E-MAIL
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="je@email.nl"
                        required
                        className="mt-1 text-base bg-[#FAEBEE]/30 border-[#854C73]/10 focus:bg-white focus:border-[#854C73] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Onderwerp */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="onderwerp" 
                      className="text-xs font-bold tracking-wider uppercase text-[#6b4912]"
                    >
                      ONDERWERP
                    </Label>
                    <Input
                      id="onderwerp"
                      name="onderwerp"
                      placeholder="Waar gaat het over?"
                      className="mt-1 text-base bg-[#FAEBEE]/30 border-[#854C73]/10 focus:bg-white focus:border-[#854C73] transition-colors"
                    />
                  </div>

                  {/* Bericht */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="bericht" 
                      className="text-xs font-bold tracking-wider uppercase text-[#6b4912]"
                    >
                      BERICHT
                    </Label>
                    <Textarea
                      id="bericht"
                      name="bericht"
                      placeholder="Typ hier je bericht..."
                      required
                      rows={6}
                      className="mt-1 text-base bg-[#FAEBEE]/30 border-[#854C73]/10 focus:bg-white focus:border-[#854C73] transition-colors resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#854C73] hover:bg-[#590D0D] text-white shadow-lg hover:shadow-xl transition-all rounded-full h-14 text-base font-medium"
                  >
                {buttonState === 'loading' && (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Wordt verzonden...
                  </>
                )}
                {buttonState === 'success' && (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Verzonden!
                  </>
                )}
                {buttonState === 'error' && (
                  <>
                    <X className="mr-2 h-5 w-5" />
                    Fout opgetreden
                  </>
                )}
                {buttonState === 'idle' && (
                  <>
                        Verstuur Bericht
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;